import React, { useState, Fragment, useEffect } from 'react'
import PropTypes from 'prop-types'
import { cloneDeep } from 'lodash'
import { makeStyles } from '@mui/styles'
import { useNavigate } from 'react-router'

import config from '@/config'
import Panel from '@/components/layouts/panel/index'
import PanelHeader from '@/components/layouts/panel/panel-header'
import PanelContent from '@/components/layouts/panel/panel-content'
import PanelFileContent from '@/modules/file/components/panels/panel-file-content'
import lang from '@/lang'
import PanelFooter from '@/components/layouts/panel/panel-footer'
import expenseConfig from '@/modules/expense/config'
import { formatCurrency } from '@/modules/_helpers/formatCurrency'
import Badge from '@/modules/base/components/badge/badge'
import { formatDate } from '@/modules/_helpers/formatDate'
import formatPercent from '@/modules/_helpers/formatPercent'
import Loader from '@/modules/base/components/loader/loader'
import ExpenseAndRevenueDetailPreview from './expense-and-revenue-detail-preview'
import ReceiptDetailAssistant from './receipt-detail-assistant'
import ExpenseService from '@/modules/expense/ExpenseService'
import RevenueService from '@/modules/revenue/revenueService'
import DOAccordion from '@/modules/base/do-accordion'
import { TagSelectInput } from '@/modules/base/components/inputs/index'
import SupplierService from '@/modules/supplier/supplierService'
import CustomerService from '@/modules/customer/customerService'
import { formatIban } from '@/modules/_helpers/formatIban'
import { toast } from '@/modules/base/components/toast/toastConfig'
import ReceiptPreviewFileListPanelFooter from '@/modules/receipt/preview/file-list/panel/receipt-preview-file-list-panel-footer'
import { getReceiptState, getReceiptStateColor } from '@/modules/receipt/_helpers/receiptState'
import { receiptStates } from '@/modules/base/util/constants'

const useStyles = makeStyles(theme => ({
  mainContainer: {
    width: '375px',
    border: theme.border,
  },
  detailContainer: {
    maxWidth: 'calc(100vw - 350px - 100px)',
    width: '1000px',
  },
  fileContent: {
    padding: '0px 30px',
    backgroundColor: theme.white,
  },
  panelFooter: {
    borderWidth: 0,
  },
  previewPanelFooter: {
    padding: 30,
    borderWidth: 0,
    '& button, a': {
      minWidth: 45,
      minHeight: 45,
      maxHeight: 'none',
      maxWidth: 'none',
    },
  },
  controls: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  panelHeader: {
    borderWidth: 0,
    borderBottom: theme.border,
    height: 85,
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    marginRight: 10,

    '& > div': {
      height: 'auto',
      '& > span': {
        display: 'unset',
        lineHeight: 0,
        marginBottom: 0,
        paddingLeft: 30,
      },
    },
  },
  previewHeader: {
    display: 'flex',
    marginRight: 'auto',
    '&:hover div ': {
      backgroundColor: theme.darkGreen,
    },
    '&:hover div > *': {
      cursor: 'pointer',
    },
    '& > *': {
      margin: '10px 0px 0px 0px',
    },
  },
  positions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: theme.fontSizes.smaller,
  },
  badge: {
    width: 'fit-content',
  },
  infoBox: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
    paddingTop: 15,
  },
  infoPositions: {
    display: 'flex',
    flexDirection: 'column',
    fontSize: theme.fontSizes.smaller,
  },
  priceRow: {
    display: 'flex',
    flexDirection: 'row',
    paddingTop: 20,
    justifyContent: 'space-between',
  },
  price: {
    color: theme.red,
    fontSize: '28px',
    lineHeight: 0.64,
    letterSpacing: 0.56,
  },
  payeeRow: {
    padding: '20px 0px 10px 0px',
    fontSize: theme.fontSizes.medium,
    lineHeight: 1,
    letterSpacing: 0.36,
    fontWeight: theme.fontWeights.medium,
  },
  contactLink: {
    cursor: 'pointer',
    color: theme.primary,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  ibanRow: {
    display: 'flex',
    flexDirection: 'row',
    '& span:first-of-type': {
      paddingRight: 10,
    },
  },
  bookingRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  bookingContent: {
    backgroundColor: theme.backgroundColor,
    border: theme.border,
    padding: '10px 10px 10px 10px',
  },
  bookingPayment: {
    fontSize: theme.fontSizes.smedium,
  },
  bookingPrice: {
    fontSize: theme.fontSizes.smedium,
    color: theme.red,
  },
  currency: {
    whiteSpace: 'nowrap',
  },
  accordion: {
    '&.MuiAccordion-root.Mui-expanded': {
      margin: '16px 0px',
    },
  },
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 15,
  },
  title: {
    display: 'flex',
    color: theme.black,
    fontSize: theme.fontSizes.smedium,
    fontWeight: theme.fontWeights.medium,
    margin: '5px 0 5px 1px',
    letterSpacing: '0.16px',
  },
}))

const ExpenseAndRevenueDetailView = (props) => {
  const classes = useStyles(props)
  const navigate = useNavigate()
  const {
    onClose,
    onChange,
    file: initFile,
    expenseId,
    revenueId,
  } = props

  const [file, setFile] = useState(cloneDeep(initFile))
  const [expense, setExpense] = useState()
  const [revenue, setRevenue] = useState()
  const [isLoading, setIsLoading] = useState(false)
  const [previewZoom, setPreviewZoom] = useState((file?.metaData?.scale || 1) * 1)
  const [state, setState] = useState([])
  const [isTypeOf, setIsTypeOf] = useState()
  const [bookingIban, setBookingIban] = useState()
  const [activeReceipt, setActiveReceipt] = useState(null)

  const expenseState = expense?.isOverDue ? receiptStates.ISOVERDUE : expense?.isDue ? receiptStates.ISDUE : expense?.state
  const parsedState = getReceiptState(expense ? expenseState : revenue?.state)
  const badgeColor = getReceiptStateColor(expense ? expenseState : revenue?.state)

  useEffect(() => {
    renderTagInput()
    setIsTypeOf(expense || revenue)
  }, [expense, revenue])

  useEffect(() => {
    setIsLoading(true)
    // Never keep Max bound to the previous row while the next quick-detail payload is loading.
    setActiveReceipt(null)
    const { receiptBaseUrl } = expenseConfig
    if (expenseId) {
      ExpenseService.getById(expenseId)
        .then(({ data: responseData }) => {
          setExpense(responseData)
          const receiptData = responseData.receipts.map(receipt => {
            return {
              ...receipt,
              url: `${receiptBaseUrl}${receipt.url}`,
            }
          })
          setFile(receiptData)
          setIsLoading(false)

          if (!responseData.isSeen) {
            ExpenseService.markAsSeen(expenseId)
          }
        })
    }
    if (revenueId) {
      RevenueService.getById(revenueId)
        .then(({ data: responseData }) => {
          setRevenue(responseData)
          const receiptData = responseData.receipts.map(receipt => {
            return {
              ...receipt,
              url: `${receiptBaseUrl}${receipt.url}`,
            }
          })
          setFile(receiptData)
          setIsLoading(false)

          if (!responseData.isSeen) {
            RevenueService.markAsSeen(revenueId)
          }
        })
    }
  }, [expenseId, revenueId])

  const zoom = (zoomStep) => {
    setPreviewZoom(Math.max(previewZoom + zoomStep, 0.25))
  }

  const onTagsChange = (tags) => {
    const labels = tags?.map(tag => tag.label)
    const newState = cloneDeep(state)
    newState.tags = labels

    setState(newState)
    if (expense) {
      const newTagData = {
        tags: newState.tags || [],
      }
      ExpenseService.updateTags(expense?.id, newTagData)
        .then(({ data }) => {
          const newLabelData = data.map(responseTag => {
            const { label } = responseTag
            return label
          })
          setExpense({ ...expense, ...{ tags: newLabelData } })
          toast.showToast({ message: lang.tag.editSuccessMessage })
          onChange && onChange()
        })
        .catch(() => toast.showToast({ type: 'error', message: lang.tag.deleteErrorMessage }))
    } if (revenue) {
      const newRevenueTagData = {
        tags: newState.tags || [],
      }
      RevenueService.updateTags(revenue?.id, newRevenueTagData)
        .then(({ data }) => {
          const newLabelData = data.map(responseTag => {
            const { label } = responseTag
            return label
          })
          setRevenue({ ...revenue, ...{ tags: newLabelData } })
          toast.showToast({ message: lang.tag.editSuccessMessage })
          onChange && onChange()
        })
        .catch(() => toast.showToast({ type: 'error', message: lang.tag.deleteErrorMessage }))
    }
  }

  const contactId = expense ? expense?.supplierId : revenue?.customerId

  // Die im Kopf angezeigte IBAN stammt aus den STAMMDATEN des Kontakts (Lieferant bzw. Kunde) —
  // nicht vom Beleg. Sie wird deshalb einmal je Kontakt nachgeladen.
  //
  // Der Abruf stand bis 16.09.2026 IM RENDER-PFAD, in `getBookingAccountIban` unten, mit dem
  // Waechter `isTypeOf?.supplierId && isLoading`. Beide sind nie gleichzeitig wahr: `isLoading`
  // wird im `.then()` des Ladeeffekts auf false gesetzt, `isTypeOf` erst vom nachgelagerten Effekt
  // auf `[expense, revenue]` — und seit React 18 (`createRoot`) batcht React die setState-Aufrufe
  // im Promise-Callback, es gibt also keinen Render dazwischen, in dem beides gilt. Der Request
  // feuerte nie, `bookingIban` blieb `undefined`, und die Zeile unten zeigte fuer JEDEN Beleg
  // „nicht hinterlegt" — auch dann, wenn der Lieferant sehr wohl eine Bankverbindung hatte.
  // Gemeldet 16.09.2026 an einer Ausgabe, deren Lieferant eine hatte.
  //
  // Der Fehler war vor dem React-Update (7c724f2e26) latent: React 16 batchte in Promise-Callbacks
  // nicht, dort entstand der Render mit `isTypeOf` gesetzt UND `isLoading` noch true.
  useEffect(() => {
    if (!contactId) {
      setBookingIban(undefined)
      return
    }
    const loadContact = expense
      ? SupplierService.getById(contactId)
      : CustomerService.getById(contactId)

    loadContact
      // Leere Liste statt `undefined`: die beiden Faelle „kein Konto hinterlegt" und „noch nicht
      // geladen" muessen unterscheidbar bleiben, sonst steht wieder ueberall dasselbe.
      .then(contact => setBookingIban((contact?.bankAccounts || []).map(({ iban }) => iban)))
      .catch(() => setBookingIban([]))
  }, [contactId, !!expense])

  const navigateToContact = () => {
    if (!contactId) return
    if (onClose) onClose()
    navigate(expense ? `/supplier/${contactId}` : `/customer/${contactId}`)
  }

  const mappedBookings = expense?.bookings.map(booking => {
    const { bookingAmount, bankAccountName, bookingDate } = booking
    return (
      <div className={classes.bookingContent}>
        <div className={classes.bookingRow}>
          <span className={classes.bookingPayment}>{lang.expenseAndRevenue.drawer.payment}</span>
          <span className={classes.bookingPrice}>{formatCurrency(bookingAmount)}</span>
        </div>
        <div className={classes.bookingRow}>
          <span>{bankAccountName}</span>
          <span>{formatDate(bookingDate, config.dateFormat.api, config.dateFormat.client)}</span>
        </div>
      </div>
    )
  })

  const getBookingAccountIban = () => {
    return (
      <div>
        <div className={classes.priceRow}>
          <span className={classes.price}>{formatCurrency(isTypeOf?.priceTotal)}</span>
          <Badge label={parsedState} color={badgeColor} customClass={classes.badge} />
        </div>
        <div className={classes.payeeRow}>
          <span
            className={contactId ? classes.contactLink : undefined}
            onClick={contactId ? navigateToContact : undefined}
          >
            {expense ? expense?.payee : revenue?.sender}
          </span>
        </div>
        <div className={classes.ibanRow}>
          <span>{lang.expenseAndRevenue.drawer.iban}</span>
          {/* `bookingIban?.[0]`, nicht `bookingIban`: eine LEERE Liste ist truthy, ein Kontakt ohne
              Bankverbindung haette also `formatIban(undefined)` gerendert — eine leere Zelle statt
              der Beschriftung. */}
          <span>{bookingIban?.[0] ? formatIban(bookingIban[0]) : lang.expenseAndRevenue.drawer.noIban}</span>
        </div>
      </div>
    )
  }

  const renderDetails = () => {
    return (
      <div>
        <div>
          {getBookingAccountIban()}
        </div>
        {mappedBookings}
        <DOAccordion
          className={classes.accordion}
          title={lang.expenseAndRevenue.drawer.description}
          content={isTypeOf?.description ? isTypeOf?.description : lang.expenseAndRevenue.drawer.descriptionDefaultText } />
        <div className={classes.infoBox}>
          <div className={classes.infoPositions}>
            <span>{lang.expenseAndRevenue.drawer.receiptNumber}</span>
            <span>{isTypeOf?.receiptNumber}</span>
          </div>
          <div className={classes.infoPositions}>
            <span>{lang.expenseAndRevenue.drawer.receiptDate}</span>
            <span>{formatDate(isTypeOf?.date, config.dateFormat.api, config.dateFormat.client)}</span>
          </div>
          <div className={classes.infoPositions}>
            <span>{lang.expenseAndRevenue.drawer.deliveryDate}</span>
            <span>{formatDate(isTypeOf?.deliveryDate, config.dateFormat.api, config.dateFormat.client)}</span>
          </div>
        </div>
      </div>
    )
  }

  const renderTagInput = () => {
    if (!expense && !revenue) return null
    return (
      <div>
        <div className={classes.titleWrapper}>
          <span className={classes.title}>{lang.expenseAndRevenue.drawer.tags}</span>
        </div>
        <TagSelectInput
          dataQsId='expenseAndRevenueDetailView-tags'
          name='tags'
          placeholder={lang.receipt.form.tags}
          value={isTypeOf?.tags?.map((item) => ({
            id: item,
            label: item,
          }))}
          category={expense ? 'expenses' : 'revenues'}
          includeSystemTags={true}
          onChange={onTagsChange}
        />
      </div>
    )
  }

  const mappedPositions = isTypeOf?.positions.map(position => {
    const { bookkeepingAccountNo, bookkeepingAccountDescriptionCustom, vatPercent, totalGross } = position
    return (
      <div className={classes.positions}>
        <span>
          {bookkeepingAccountNo ? `(${bookkeepingAccountNo}) ` : ''}
          {bookkeepingAccountDescriptionCustom || lang.expenseAndRevenue.drawer.withoutCategoryLabel} {formatPercent(vatPercent)}
        </span>
        <span className={classes.currency}>{formatCurrency(totalGross)}</span>
      </div>
    )
  })

  return (
    <Fragment>
      <Loader visible={isLoading} text={lang.generics.loading} />
      {file?.length
        ? <ExpenseAndRevenueDetailPreview
          classNames={classes.detailContainer}
          file={file}
          onZoom={zoom}
          onActiveFileChange={setActiveReceipt}
          downloadDisabled={true}
          zoom={previewZoom} />
        : null}
      <Panel className={classes.mainContainer}>
        <PanelHeader
          onClose={onClose}
          className={classes.panelHeader}
          displayClose title={expense ? lang.expenseAndRevenue.drawer.expenseDetails : lang.expenseAndRevenue.drawer.revenueDetails} />
        <PanelContent>
          <PanelFileContent className={classes.fileContent}>
            <div className={classes.controls}>
              {renderDetails()}
              {mappedPositions?.length > 0
                && <div className={classes.avvAccordion}>
                  <DOAccordion className={classes.accordion} title={lang.expenseAndRevenue.drawer.positions} content={mappedPositions} expanded />
                </div>
              }
              {renderTagInput()}
            </div>
          </PanelFileContent>
        </PanelContent>
        {/* FLAG:useDocuAi/docuAiEnhancements — one shared assistant band for booked expenses AND
            revenues. It follows the visible carousel receipt and hydrates the full DMS file only
            while both flags are active; RagChatSection's header includes the report action. */}
        <ReceiptDetailAssistant receipt={activeReceipt} />
        <PanelFooter className={classes.panelFooter}>
          <ReceiptPreviewFileListPanelFooter
            hideDownloadButton={true}
            showContextMenu={true}
            className={classes.previewPanelFooter}
            isDetailView={true}
            hideCancelButton={true}
            file={file}
            expense={expense}
            revenue={revenue}
            isEconomicAsset={!!expense?.economicAssetId}
            onAbort={onClose}
          />
        </PanelFooter>
      </Panel>
    </Fragment>
  )
}

ExpenseAndRevenueDetailView.propTypes = {
  onClose: PropTypes.func,
  onChange: PropTypes.func,
  file: PropTypes.object,
  expenseId: PropTypes.string,
  revenueId: PropTypes.string,
}

export default ExpenseAndRevenueDetailView
